<template>
    <div class="relative z-30" aria-labelledby="modal-title" role="dialog" aria-modal="true">
        <div class="fixed inset-0 bg-black bg-opacity-25 backdrop-blur-sm transition-opacity"></div>
        <div class="fixed inset-0 z-30 overflow-y-auto">
            <div class="flex min-h-full items-end px-2 pt-1 pb-16 text-center max-w-lg mx-auto">
                <div class="border-2 border-jackpot-bg flex-1 relative transform overflow-hidden rounded-lg text-left drop-shadow-xl transition-all">
                    <div class="bg-white dark:bg-global-bg p-4">
                        <div class="text-center">
                            <h3 class="text-lg font-medium leading-6 text-gray-900 dark:text-gray-50">Change PFP</h3>
                            <div class="mt-2 flex items-center justify-center text-gray-400 dark:text-gray-500">
                                <div class="relative">
                                    <img
                                        class="block rounded-full object-cover w-10 h-10 sm:w-14 sm:h-14 border border-white"
                                        v-if="pfp"
                                        :src="pfp" />
                                    <svg v-else xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" class="w-10 h-10 sm:w-14 sm:h-14">
                                        <path fill-rule="evenodd" d="M18.685 19.097A9.723 9.723 0 0021.75 12c0-5.385-4.365-9.75-9.75-9.75S2.25 6.615 2.25 12a9.723 9.723 0 003.065 7.097A9.716 9.716 0 0012 21.75a9.716 9.716 0 006.685-2.653zm-12.54-1.285A7.486 7.486 0 0112 15a7.486 7.486 0 015.855 2.812A8.224 8.224 0 0112 20.25a8.224 8.224 0 01-5.855-2.438zM15.75 9a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0z" clip-rule="evenodd" />
                                    </svg>
                                    <div
                                        class="absolute -top-2 -right-3 px-1 bg-white dark:bg-black bg-opacity-50 rounded-full"
                                        v-if="pfp"
                                        @click="removePfp">
                                        <i class="fa-solid fa-xmark fa-fw text-sm text-red-400 dark:text-red-600"></i>
                                    </div>
                                </div>
                            </div>
                            <div v-if="!fetching && pfps.length" class="mt-4 text-gray-800 dark:text-gray-300 text-sm max-[320px]:text-xs">
                                Available PFPs...
                            </div>
                            <div v-else-if="!fetching && !pfps.length" class="mt-4 text-red-800 dark:text-red-400 text-sm max-[320px]:text-xs">No PFPs available!</div>
                            <div class="mt-4 flex items-center justify-center flex-wrap gap-2 sm:gap-4 mx-auto">
                                <div v-if="fetching" class="text-black dark:text-white flex flex-col items-center justify-center gap-2">
                                    <spinner />
                                    <div class="text-xs text-gray-800 dark:text-gray-200">Fetching PFPs...</div>
                                </div>
                                <div v-if="!fetching" @click="select(item.content.links.image)" v-for="item in pfps" :key="item.id" class="flex flex-col gap-1 items-center justify-center w-14 max-[320px]:w-12">
                                    <img :src="item.content.links.image" class="block rounded-full object-cover h-12 w-12 max-[320px]:h-10 max-[320px]:w-10 border border-white">
                                    <div class="text-xs w-14 truncate text-black dark:text-inner-border">{{ item.content.metadata.name }}</div>
                                </div>
                            </div>
                            <div v-if="showNav" class="mt-4 flex items-center justify-center gap-2 text-xs text-gray-900 dark:text-gray-200">
                                <button @click="getNfts(prevPage)" :disabled="page == 1" class="text-sm rounded drop-shadow bg-jackpot-bg text-white disabled:opacity-25">
                                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" class="w-5 h-5">
                                        <path fill-rule="evenodd" d="M12.79 5.23a.75.75 0 01-.02 1.06L8.832 10l3.938 3.71a.75.75 0 11-1.04 1.08l-4.5-4.25a.75.75 0 010-1.08l4.5-4.25a.75.75 0 011.06.02z" clip-rule="evenodd" />
                                    </svg>
                                </button>
                                <div class="font-semibold">{{ page }}</div>
                                <button @click="getNfts(nextPage)" :disabled="lastPage" class="text-sm rounded drop-shadow bg-jackpot-bg text-white disabled:opacity-25">
                                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" class="w-5 h-5">
                                        <path fill-rule="evenodd" d="M7.21 14.77a.75.75 0 01.02-1.06L11.168 10 7.23 6.29a.75.75 0 111.04-1.08l4.5 4.25a.75.75 0 010 1.08l-4.5 4.25a.75.75 0 01-1.06-.02z" clip-rule="evenodd" />
                                    </svg>
                                </button>
                            </div>
                        </div>
                    </div>
                    <div class="bg-gray-50 dark:bg-global-bg px-4 py-2 flex flex-col">
                        <button
                            :disabled="fetching || !differentPfp"
                            @click.prevent="submit"
                            type="button"
                            class="modal-action-button">Change PFP</button>
                        <button
                            @click.prevent="close"
                            type="button"
                            class="modal-cancel-button">Cancel</button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import axios from 'axios'
import { useWallet } from 'solana-wallets-vue'

export default {
    name: 'ChangePfpModal',
    props: ['account', 'token'],
    data() {
        return {
            rpc: process.env.MIX_SOLANA_RPC,
            fetching: false,
            originalPfp: null,
            pfp: null,
            pfps: [],
            limit: 8,
            page: 1,
            total: 0,
        }
    },
    computed: {
        differentPfp() {
            return this.pfp !== this.originalPfp
        },
        currentPfp() {
            return this.account ? this.account.pfp : null
        },
        prevPage() {
            const page = this.page - 1
            return page < 1 ? 1 : page
        },
        nextPage() {
            const nextPage = this.page + 1
            return this.lastPage ? this.page : nextPage
        },
        lastPage() {
            return this.total === 0 || this.total < this.limit
        },
        showNav() {
            return this.pfps.length > 0 && !(this.lastPage && this.page === 1)
        },
    },
    async mounted() {
        this.pfp = this.currentPfp
        this.originalPfp = this.pfp
        await this.getNfts(this.page)
    },
    methods: {
        removePfp() {
            this.pfp = null
        },
        select(image) {
            this.pfp = image
        },
        async getNfts(page) {
            this.fetching = true
            const config = {
                headers: {
                    'Content-Type': 'application/json'
                }
            }
            const data = {
                jsonrpc: '2.0',
                id: 1,
                method: 'getAssetsByOwner',
                params: {
                    ownerAddress: useWallet().publicKey.value.toString(),
                    page: page,
                    limit: this.limit,
                },
            }
            let axiosInstance = axios.create();
            delete axiosInstance.defaults.headers.common["X-Requested-With"];
            axiosInstance
                .post(this.rpc, data, config)
                .then((response) => {
                    this.page = response.data.result.page
                    this.total = response.data.result.total
                    this.pfps = response.data.result.items
                })
                .catch((err) => {
                    console.log(err)
                })
                .then(() => {
                    this.fetching = false
                })
        },
        submit() {
            if (this.pfp == this.account.pfp) {
                return
            }
            window.axios.post('/api/account/pfp', {
                'pfp': this.pfp
            }, {
                headers: {
                    'Authorization': `Bearer ${this.token}`
                }
            })
                .then((res) => {
                    this.pfp = res.data.pfp
                    this.emitter.emit('change-pfp', this.pfp)
                    this.$toast.success('PFP changed')
                })
                .catch((err) => {
                    if (err.response && err.response.status == 401) {
                        this.$toast.error('You need to login.')
                    } else {
                        this.$toast.error(err.message)
                    }
                }).then(() => {
                    this.pfp = null
                    this.emitter.emit('close-modal', 'pfp_modal')
                })
        },
        close() {
            this.emitter.emit('close-modal', 'pfp_modal')
        }
    }
}
</script>
